import {ColumnAggregationType} from "utils/enums";
import {HighlightColor, HighlightRange, Nullable, IColumnsWithValuesProps} from "./interfaces";

const isInRange = (value: number | null | undefined, range: HighlightRange) => {
    if (value == null) return false;

    const rMin = range.min?.value ?? Number.MIN_SAFE_INTEGER;
    const rMax = range.max?.value ?? Number.MAX_SAFE_INTEGER
    const minInclusive = range.min?.inclusive ?? false
    const maxInclusive = range.max?.inclusive ?? false


    if (rMin > value || (!minInclusive && rMin === value)) return false

    return !(rMax < value || (!maxInclusive && rMax === value));
}

const getValueColors = (value: number|undefined, {
    defaultHighlightColor,
    highlightRanges
}: { highlightRanges?: Nullable<Array<HighlightRange>>, defaultHighlightColor?: Nullable<HighlightColor> }) => {
    let bgColor;
    let color;

    if (highlightRanges&&value) {
        for (const range of highlightRanges) {
            if (isInRange(value, range)) {
                range.color && (color = range.color);
                range.bgColor && (bgColor = range.bgColor);
                break
            }
        }
    }

    if (defaultHighlightColor && !bgColor && !color) {
        defaultHighlightColor.color && (color = defaultHighlightColor.color);
        defaultHighlightColor.bgColor && (bgColor = defaultHighlightColor.bgColor);
    }

    return [color, bgColor];
}

const getHighlightColor = (highlightColor?: Nullable<HighlightColor>) => {
    const bgColor = highlightColor?.bgColor;
    const color = highlightColor?.color;

    return [color, bgColor];
}

const checkForUnwantedType = (type: string | undefined) => {
    return !(type === "USER_SIGNATURE" || type === "PARAMETERS" || type === "FOREIGN_KEY")
}

const checkForNumberType = (type: string | undefined) => {
    return type === "DOUBLE" || type === "LONG"
}

const getDataStringParamsToObject = (dateFrom?: string, dateTo?: string, filter?: string) => {
    if (dateFrom || dateTo || filter) {
        const tempSearchParams: { dateFrom?: string, dateTo?: string, filter?: string } = {};
        if (dateFrom) {
            tempSearchParams.dateFrom = dateFrom;
        }
        if (dateTo) {
            tempSearchParams.dateTo = dateTo;
        }
        if (filter) {
            tempSearchParams.filter = filter;
        }
        return {params: tempSearchParams}
    }
    return undefined
}

const filterPreviewValidation = (dateFrom?: string, dateTo?: string, filter?: string): { params: any } | undefined => {
    if (!dateFrom && !dateTo && !filter) {
        return undefined
    }
    if(dateFrom && !dateTo && !filter){
        return undefined
    }
    if(!dateFrom && dateTo && !filter){
        return undefined
    }

    const tempSearchParams: { dateFrom?: string, dateTo?: string, filter?: string } = {};
    if (dateFrom) {
        tempSearchParams.dateFrom = dateFrom;
    }
    if (dateTo) {
        tempSearchParams.dateTo = dateTo;
    }
    if (filter) {
        tempSearchParams.filter = filter;
    }

    return {params: tempSearchParams}
}

const arrayToCsv = (arr: Array<any>) => {
    if (!arr) {
        return '';
    }
    let csv = `${arr[0].map((item: any) => item.name).join(',')  }\n`;
    for (let i = 0; i < arr.length; i++) {
        csv += `${arr[i].map((item: any) => `"${item.value}"`).join(',')  }\n`;
    }
    return csv;
}

const downloadBlob = (content: any, filename: string, contentType: string) => {
    const blob = new Blob([content], {type: contentType});
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

const s2ab = (s: any) => {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);

    for (let i = 0; i < s.length; i++) {
        view[i] = s.charCodeAt(i) & 0xff;
    }

    return buf;
}

const extractNumber = (value: string | number | undefined): number => {
    if (!value) {
        return 0
    }
    if (typeof value === "string") {
        const parsedNumber = parseFloat(value);
        if (!isNaN(parsedNumber)) {
            return parsedNumber;
        } 
            return 400;
        
    }

    return value
}

const getChartHeight = (heights: {
    configHeight?: number | string,
    divHeight?: number,
    functionsHeight?: number
}, dashboardComponent = true): number => {
    if (heights.configHeight != null) {
        return extractNumber(heights.configHeight)
    } if (!dashboardComponent && (heights.divHeight && heights.functionsHeight && heights.divHeight >= 0 && heights.functionsHeight >= 0)) {
        return heights.divHeight - heights.functionsHeight - 4
    } 
        return 400;
    
}

const columnsTotalsAggregation = (columns: IColumnsWithValuesProps[]) => {
    return columns.map((col) => {
            if (col.aggregationType === ColumnAggregationType.SUM) {
                col.total = col.values.reduce((a: number, b: number) => a + b, 0);
            }

            if (col.aggregationType === ColumnAggregationType.MEAN) {
                col.total = (col.values.reduce((a: number, b: number) => a + b, 0) / col.values.length);
            }

            if (col.aggregationType === ColumnAggregationType.COUNT) {
                col.total = col.values.length;
            }
            return col;
        }
    );
};

const handlePrecision = (number: number, precision: number, output: boolean = false) => {
    try {
        if (output) {
            return new Intl.NumberFormat('lt-LT', {
                minimumFractionDigits: precision,
                maximumFractionDigits: precision
            })
                .format(number)
        } 
            return number.toFixed(precision);
        
    } catch (err) {
        return number;
    }
}

const formatDataPatternToCalendar = (datePattern: string) => {
  const parsedFormat = datePattern.split(" ");
  const newFormat = parsedFormat[0].replaceAll("yyyy", "yy").replaceAll("MM", "mm");
  return newFormat;
};

export {
  isInRange,
  getValueColors,
  getHighlightColor,
  checkForUnwantedType,
  checkForNumberType,
  getDataStringParamsToObject,
  filterPreviewValidation,
  arrayToCsv,
  downloadBlob,
  s2ab,
  extractNumber,
  getChartHeight,
  columnsTotalsAggregation,
  handlePrecision,
  formatDataPatternToCalendar,
};